import { Link } from '@tanstack/react-router';
import { useDraftInvoiceStore } from '@/stores/draftInvoiceStore';
import { Button } from '@/components/ui/button';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useState } from 'react';

export function CartDropdown() {
  const { items, updateQuantity, removeItem, getTotalItems } = useDraftInvoiceStore();
  const [isOpen, setIsOpen] = useState(false);

  const totalItems = getTotalItems();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const subtotal = item.quantity * item.unitPrice;
      const vat = subtotal * (item.vat / 100);
      return sum + subtotal + vat;
    }, 0);
  };

  if (totalItems === 0) return null;

  return (
    <div className="relative">
      {/* Cart Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-emerald-600 hover:text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors"
      >
        <ShoppingBag className="w-5 h-5" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {totalItems}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Content */}
          <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border z-50 max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  فاتورة مسودة / Facture brouillon
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white rounded transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {totalItems} {totalItems === 1 ? 'منتج / produit' : 'منتجات / produits'}
              </p>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {items.map((item) => (
                <div
                  key={item.productId}
                  className="flex gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-gray-900 truncate">
                      {item.productName}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(item.unitPrice)} · TVA {item.vat}%
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        if (item.quantity > 1) {
                          updateQuantity(item.productId, item.quantity - 1);
                        }
                      }}
                      disabled={item.quantity <= 1}
                      className="p-1 hover:bg-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Minus className="w-3 h-3 text-gray-600" />
                    </button>
                    <span className="w-8 text-center text-sm font-medium">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                      className="p-1 hover:bg-white rounded"
                    >
                      <Plus className="w-3 h-3 text-gray-600" />
                    </button>
                  </div>

                  <button
                    onClick={() => removeItem(item.productId)}
                    className="p-1 hover:bg-red-100 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <span className="font-semibold text-gray-900">
                  المجموع / Total:
                </span>
                <span className="font-bold text-lg text-emerald-600">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>

              <Link to="/draft-invoice" onClick={() => setIsOpen(false)}>
                <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                  إكمال الفاتورة / Compléter la facture
                </Button>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
